(() => {
  const VERSION = 'v5';
  const SIZE_NAMES = new Set(['size', 'shoe size', 'المقاس', 'مقاس']);

  const norm = (value) => String(value ?? '').trim();

  const findSizeGroup = (productInfo) => {
    const picker = productInfo?.querySelector('variant-selects');
    if (!picker) return null;

    return [...picker.querySelectorAll('[data-option-name]')].find((group) =>
      SIZE_NAMES.has(norm(group.dataset.optionName).toLowerCase())
    ) || null;
  };

  const hasSelectedSize = (group) => {
    if (!group) return false;

    const radios = [...group.querySelectorAll('input[type="radio"]')];
    if (radios.length) return radios.some((input) => input.checked && !input.disabled);

    const select = group.querySelector('select');
    return Boolean(select && select.selectedIndex >= 0 && select.value);
  };


  const isAwaitingSize = (productInfo, group) => {
    const ux = window.ShoppingoooVariantUX;
    if (ux?.hasSize?.(productInfo) && typeof ux.awaitingSize === 'function') {
      return Boolean(ux.awaitingSize(productInfo));
    }
    return group?.dataset.shoppingoooUserSizeExplicit !== 'true';
  };

  const clearUncommittedSize = (group) => {
    group?.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
      input.setAttribute('aria-checked', 'false');
    });
    const select = group?.querySelector('select');
    if (select) {
      [...select.options].forEach((option) => option.removeAttribute('selected'));
      select.selectedIndex = -1;
    }
    const display = group?.querySelector('[data-selected-value]');
    if (display) display.textContent = '';
  };

  const getButton = (productInfo) =>
    productInfo?.querySelector('product-form button[type="submit"], product-form [type="submit"]') || null;

  const forceChooseSizeState = (productInfo) => {
    const sizeGroup = findSizeGroup(productInfo);
    const button = getButton(productInfo);
    if (!sizeGroup || !button) return;
    if (!isAwaitingSize(productInfo, sizeGroup)) return;

    clearUncommittedSize(sizeGroup);
    button.removeAttribute('disabled');
    button.removeAttribute('aria-disabled');
    button.style.setProperty('opacity', '1', 'important');

    const text = button.querySelector('span');
    if (text && text.textContent.trim() !== (document.documentElement.lang.startsWith("ar") ? "اختاري مقاسك" : "Choose size")) {
      text.textContent = (document.documentElement.lang.startsWith("ar") ? "اختاري مقاسك" : "Choose size");
    }

    button.dataset.shoppingoooChooseSizePrompt = VERSION;
  };

  const installClickGuard = () => {
    if (document.documentElement.dataset.shoppingoooChooseSizeClickGuard === VERSION) return;

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('product-info product-form button[type="submit"], product-info product-form [type="submit"]') : null;
      if (!target) return;

      const productInfo = target.closest('product-info');
      const sizeGroup = findSizeGroup(productInfo);
      if (!sizeGroup || !isAwaitingSize(productInfo, sizeGroup)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      forceChooseSizeState(productInfo);
      sizeGroup.style.setProperty('outline', '2px solid #b3261e');
      sizeGroup.style.setProperty('outline-offset', '6px');
      sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.ShoppingoooVariantUX?.requestSize?.(productInfo);
    }, true);

    document.documentElement.dataset.shoppingoooChooseSizeClickGuard = VERSION;
  };

  const install = (productInfo) => {
    if (!productInfo || productInfo.dataset.shoppingoooChooseSizeInstalled === VERSION) {
      forceChooseSizeState(productInfo);
      return;
    }

    const sizeGroup = findSizeGroup(productInfo);
    const button = getButton(productInfo);
    const form = productInfo.querySelector('product-form form');
    if (!sizeGroup || !button || !form) return;

    const buttonObserver = new MutationObserver(() => {
      forceChooseSizeState(productInfo);
    });

    buttonObserver.observe(button, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
    });

    const picker = productInfo.querySelector('variant-selects');
    picker?.addEventListener('change', (event) => {
      const liveSizeGroup = findSizeGroup(productInfo);
      if (!liveSizeGroup) return;

      const syncAfterVariantState = () => {
        if (isAwaitingSize(productInfo, liveSizeGroup)) {
          forceChooseSizeState(productInfo);
          return;
        }
        liveSizeGroup.style.removeProperty('outline');
        liveSizeGroup.style.removeProperty('outline-offset');
        button.removeAttribute('data-shoppingooo-choose-size-prompt');
        button.style.removeProperty('opacity');
      };

      if (!liveSizeGroup.contains(event.target)) {
        liveSizeGroup.dataset.shoppingoooUserSizeExplicit = 'false';
      }
      setTimeout(syncAfterVariantState, 0);
      setTimeout(syncAfterVariantState, 120);
    }, true);

    form.addEventListener('submit', (event) => {
      const liveSizeGroup = findSizeGroup(productInfo);
      if (!liveSizeGroup || !isAwaitingSize(productInfo, liveSizeGroup)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      forceChooseSizeState(productInfo);
      liveSizeGroup.style.setProperty('outline', '2px solid #b3261e');
      liveSizeGroup.style.setProperty('outline-offset', '6px');
      liveSizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);

    productInfo.dataset.shoppingoooChooseSizeInstalled = VERSION;
    forceChooseSizeState(productInfo);
  };

  const scan = (scope = document) => {
    if (scope.matches?.('product-info')) install(scope);
    scope.querySelectorAll?.('product-info').forEach(install);
  };

  const boot = () => {
    document.addEventListener('change', (event) => {
      const group = event.target.closest?.('[data-option-name]');
      if (!group || !SIZE_NAMES.has(norm(group.dataset.optionName).toLowerCase())) return;
      if (event.isTrusted && event.target.matches('input[type="radio"], select')) {
        group.dataset.shoppingoooUserSizeExplicit = String(hasSelectedSize(group));
      }
    }, true);
    installClickGuard();
    scan(document);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('product-info:loaded', (event) => scan(event.target));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();


